import { slashJoin } from 'path-slashes';

import { CURRENT_HOST_ID } from '../host-identification/current-host-id';
import { HostId } from '../host-identification/host-id.enum';

import { Env, type MultipleEnvConfig, type ScriptDomain, type UrlEnv, type Urls } from './config.model';

export const ASSET_SERVER_BASE_PATH = 'https://assets.oneweb.mercedes-benz.com/plugin';
export const ASSET_SERVER_BASE_PATH_PPROD = 'https://preprod.assets.oneweb.mercedes-benz.com/plugin';
export const ASSET_SERVER_BASE_PATH_TEST = 'https://test.assets.oneweb.mercedes-benz.com/plugin';
export const ASSET_SERVER_BASE_PATH_INT = 'https://int.assets.oneweb.mercedes-benz.com/plugin';
export const ASSET_SERVER_BASE_PATH_DEV = 'https://dev.assets.oneweb.mercedes-benz.com/plugin';

// Note: intentionally we load WB6 (not the most recent WB version) - only for standalone -
// as (currently) 3rd party libs (like FCIS) might (still) dependent on a former workbench version.
// This can be eliminated once all 3rd party libs have been migrated to WB7. Although we need
// to consider such a mechanism again with a WB8 (and higher) migration.
const LATEST_WORKBENCH_SIX_VERSION = '6-latest';
const LEGACY_WORKBENCH_PATH = slashJoin(ASSET_SERVER_BASE_PATH, 'workbench/core', LATEST_WORKBENCH_SIX_VERSION);

export const URLS: Urls = {
  apis: {
    owcc: {
      [Env.Prod]: 'https://owcc-backend.owcc-prod.prd.euc1.caas.oneweb.mercedes-benz.com/api/v3',
      prodCached: 'https://configurator.mercedes-benz.com/cc-backend/api/v3',
      [Env.Int]: 'https://owcc-backend.owcc-nonprod.nprd.euc1.caas.oneweb.mercedes-benz.com/api/v3',
      local: 'http://localhost:8098/api/v3',
      ffgDev: 'https://dev.api.oneweb.mercedes-benz.com/cc-backend/api/v3',
      ffgProd: 'https://api.oneweb.mercedes-benz.com/cc-backend/api/v3'
    },
    carl: {
      [Env.Prod]: 'https://carl.owcc-prod.prd.euc1.caas.oneweb.mercedes-benz.com/compilations',
      prodCached: 'https://configurator.mercedes-benz.com/cc-backend/carl/compilations',
      [Env.Int]: 'https://carl.owcc-nonprod.nprd.euc1.caas.oneweb.mercedes-benz.com/compilations'
    },
    deeplinks: {
      [Env.Prod]: 'https://api.oneweb.mercedes-benz.com/vehicle-deeplinks-api/v1',
      [Env.Int]: 'https://int.api.oneweb.mercedes-benz.com/vehicle-deeplinks-api/v1',
      [Env.Test]: 'https://test.api.oneweb.mercedes-benz.com/vehicle-deeplinks-api/v1'
    },
    ocapi: {
      [Env.Prod]: 'https://ocapi.api.mercedes-benz.com/v3',
      [Env.Test]: 'https://test.ocapi.api.mercedes-benz.com/v3',
      dev: 'https://dev.ocapi.api.mercedes-benz.com/v3'
    },

    /* One Mirror */
    omds: {
      [Env.Prod]: 'https://omds.onemirror.corpinter.net',
      [Env.Int]: 'https://omds-int.onemirror-nonprod.corpinter.net'
    },
    omdsRelayService: {
      [Env.Prod]: 'wss://om-relayservice.onemirror.corpinter.net',
      [Env.Int]: 'wss://om-relayservice-int.onemirror-nonprod.corpinter.net'
    }
  },
  scripts: {
    compareTeaser: slashJoin(ASSET_SERVER_BASE_PATH, 'sccs-compare-teaser/latest/compare-teaser/compare-teaser.esm.js'),
    compare: slashJoin(ASSET_SERVER_BASE_PATH, '/sccs-compare/latest/index.js'),
    upsellingRecommendation: {
      [Env.Prod]: slashJoin(ASSET_SERVER_BASE_PATH, 'boe-upsell-recommendation/latest/index.wc.js'),
      [Env.Preprod]: slashJoin(ASSET_SERVER_BASE_PATH_PPROD, 'boe-upsell-recommendation/latest/index.wc.js')
    },
    aemForms: {
      [Env.Prod]: slashJoin(ASSET_SERVER_BASE_PATH, '/mbmxp-aemf-form-container/latest/loader.js'),
      [Env.Preprod]: slashJoin(ASSET_SERVER_BASE_PATH_PPROD, '/mbmxp-aemf-form-container/latest/loader.js'),
      [Env.Int]: slashJoin(ASSET_SERVER_BASE_PATH_INT, 'mbmxp-aemf-form-container/latest/loader.js'),
      [Env.Test]: slashJoin(ASSET_SERVER_BASE_PATH_TEST, 'mbmxp-aemf-form-container/latest/loader.js')
    },
    wishlist: {
      [Env.Prod]: slashJoin(ASSET_SERVER_BASE_PATH, 'eqpodc-save-button/latest/save-button/save-button.esm.js'),
      [Env.Preprod]: slashJoin(ASSET_SERVER_BASE_PATH_PPROD, 'eqpodc-save-button/latest/save-button/save-button.esm.js')
    },
    energyConsumptionLabeling: slashJoin(ASSET_SERVER_BASE_PATH, 'owcc-energy-consumption-labeling/latest/index.wc.js'),
    fcis: slashJoin(ASSET_SERVER_BASE_PATH, 'fcis-widget/latest/fcis-widget/fcis-widget.esm.js'),
    priceDisplay: slashJoin(ASSET_SERVER_BASE_PATH, 'pls-pls-ui/latest/index.wc.js'),
    legacyWorkbench: slashJoin(LEGACY_WORKBENCH_PATH, 'workbench/workbench.esm.js')
  },
  misc: {
    assets: slashJoin(ASSET_SERVER_BASE_PATH, 'owcc/assets'),
    // the mb4.me url shortener service handles redirects to respective market sites depending on browser language settings
    modelSelection: 'https://mb4.me/your-mercedes'
  },
  styles: {
    legacyWorkbench: slashJoin(LEGACY_WORKBENCH_PATH, 'css/globals.css')
  }
};

export function getScriptUrl(domain: ScriptDomain): string {
  return URLS.scripts[domain] as string;
}
export function getEnvSpecificScriptUrl(domain: ScriptDomain, env: UrlEnv = Env.Prod): string {
  return (URLS.scripts[domain] as MultipleEnvConfig)[env] ?? (URLS.scripts[domain] as MultipleEnvConfig)[Env.Prod];
}

export const ASSET_SERVER_URL = URLS.misc.assets;

export const mapHostIdToEnv = (hostId = CURRENT_HOST_ID): Env => {
  type MappedHostId = HostId.PreProd | HostId.Int | HostId.Test | HostId.Dev;

  const mapping = {
    [HostId.PreProd]: Env.Preprod,
    [HostId.Int]: Env.Int,
    [HostId.Test]: Env.Test,
    [HostId.Dev]: Env.Test
  };

  return mapping[hostId as MappedHostId] ?? Env.Prod;
};
